import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { isFutureEffectiveDate } from '~/business-logic/billingLogic';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import { formatShortMonthUSDate } from '~/utils';
import { billingPeriodTextsPath, extendedRecurrencePeriodWord, preapprovalPeriodWord } from '~/utils/billing-period-text';
import { utils } from '..';
export const billingBadgesTextsPath = 'billing.badges';
export const badgesTextsPath = billingPeriodTextsPath;
export const ServiceDescriptionBadgesTestIds = pathgen('ServiceDescriptionBadgesTestIds');
function optionalBadge(service, badgesTexts) {
    return serviceModel.isOptional(service.participation) ? {
        skin: 'warning',
        intent: 'optional',
        text: badgesTexts.optional.text(),
        tooltip: badgesTexts.optional.tooltip()
    } : null;
}
function pausedBadge(service, badgesTexts) {
    return serviceBillingModel.isPaused(service.billing) ? {
        skin: 'warning',
        intent: 'paused',
        text: badgesTexts.paused.text(),
        tooltip: badgesTexts.paused.tooltip()
    } : null;
}
function proratedBadge(service, badgesTexts) {
    return serviceBillingModel.isProrated(service.billing) ? {
        skin: 'warning',
        intent: 'prorated',
        text: badgesTexts.prorated.text(),
        tooltip: badgesTexts.prorated.tooltip()
    } : null;
}
function preapprovedBadge(service, billingTexts, costDisplayTexts) {
    const { cost } = service;
    const preapprovalPeriod = serviceBillingModel.preapprovalPeriod(service.billing);
    if (preapprovalPeriod === PreapprovalPeriodType.None) {
        return null;
    }
    if (!serviceCostModel.isVariablePrice(cost) || !cost.unitCap || !cost.unitCap.gt(0)) {
        return null;
    }
    const badgesTexts = billingTexts.badges;
    const periodTexts = billingTexts.periods;
    const period = preapprovalPeriod ? preapprovalPeriodWord(preapprovalPeriod, periodTexts) : extendedRecurrencePeriodWord(serviceBillingModel.recurrencePeriod(service.billing) || 'onetime', periodTexts);
    const count = utils.formatNumberWithCommas(cost.unitCap);
    const unit = serviceCostModel.pluralizedUnitName(cost, cost.unitCap.toNumber(), costDisplayTexts);
    const text = period ? badgesTexts.preapprovedWithPeriod({
        count,
        unit,
        period
    }) : badgesTexts.preapproved({
        count,
        unit
    });
    return {
        skin: 'success',
        intent: 'preapproved',
        text
    };
}
export function discountBadge(service, badgesTexts) {
    const activeDiscount = serviceModel.discount(service);
    const expiredDiscount = serviceModel.expiredDiscount(service);
    const discount = activeDiscount || expiredDiscount;
    if (!discount || !discount.amount().gt(0)) {
        return null;
    }
    const expiration = discount.expiration();
    const isActiveDiscount = !!activeDiscount && (expiration === null || expiration === void 0 ? void 0 : expiration.isActive());
    const isExpiredDiscount = !!expiredDiscount;
    if (isActiveDiscount && expiration) {
        const expirationDate = expiration.date();
        const expirationPeriod = expiration.period();
        const isExpirationPeriodType = expiration.isExpirationPeriodType();
        if (expirationDate) {
            return {
                skin: 'success',
                intent: "limited-".concat(discount.type(), "-discount"),
                text: badgesTexts.discount.limitedDate({
                    amount: discount.format(),
                    date: formatShortMonthUSDate(expirationDate)
                })
            };
        }
        if (isExpirationPeriodType && expirationPeriod) {
            const discountAmount = discount.format();
            const count = expiration.amount();
            return {
                skin: 'success',
                intent: "limited-".concat(discount.type(), "-discount"),
                text: badgesTexts.discount.limitedPeriod[expirationPeriod]({
                    amount: discountAmount,
                    count
                })
            };
        }
    }
    if (isExpiredDiscount && expiration) {
        return {
            skin: 'success',
            intent: "expired-".concat(discount.type(), "-discount"),
            text: badgesTexts.discount.expired({
                amount: discount.format()
            })
        };
    }
    return {
        skin: 'success',
        intent: "".concat(discount.type(), "-discount"),
        text: badgesTexts.discount.standard({
            amount: discount.format()
        })
    };
}
function limitedTimesBadge(service, badgesTexts, commonTexts) {
    return serviceModel.maxChargesEnabled(service) ? {
        skin: 'warning',
        intent: 'limit',
        text: badgesTexts.canBeBilled({
            limit: serviceBillingModel.formatMaxChargesLimit(service.billing, commonTexts)
        }),
        tooltip: badgesTexts.limitedCharges.tooltip()
    } : null;
}
function priceIncreaseBadge(service, badgesTexts) {
    return serviceModel.priceIncrease(service).exists() ? {
        skin: 'warning',
        intent: 'price-increase',
        text: badgesTexts.priceIncrease()
    } : null;
}
export function billingDetailsBadge(service, options) {
    const { billing } = service;
    const { badgesTexts, effectiveDate = null, now, isClientPreview, isActiveAgreement } = options;
    const maxCharges = serviceModel.maxChargesEnabled(service) ? serviceModel.maxChargesLimit(service) : null;
    const getRecurringTextForClientPreview = (period)=>maxCharges ? badgesTexts.billedRecurringWithTimes({
            period,
            times: maxCharges
        }) : badgesTexts.billedRecurring({
            period
        });
    const getOnDemandTextForClientPreview = ()=>maxCharges ? badgesTexts.billedOnDemandWithTimes({
            times: maxCharges
        }) : badgesTexts.billedOnDemand();
    return serviceBillingModel.accept(billing, {
        visitOnApproval: ()=>{
            if (isFutureEffectiveDate(effectiveDate, now)) {
                return {
                    text: badgesTexts.billedOnce.onEffectiveDate(),
                    intent: 'type-effective-date'
                };
            } else if (isActiveAgreement) {
                return {
                    text: badgesTexts.billedOnce.immediately(),
                    intent: 'type-approval-amendment'
                };
            }
            return {
                text: badgesTexts.billedOnce.onProposalAcceptance(),
                intent: 'type-approval'
            };
        },
        visitOneTimeManual: ()=>({
                text: isClientPreview ? badgesTexts.billedOnce.base() : badgesTexts.billedOnce.manually(),
                intent: 'type-onetime-manual'
            }),
        visitRepeatableManual: (billing)=>({
                text: isClientPreview ? getRecurringTextForClientPreview(billing.recurrencePeriod) : badgesTexts.billedRecurringManually({
                    period: billing.recurrencePeriod
                }),
                intent: 'type-repeatable-manual'
            }),
        visitOneTimeInStages: (billing)=>({
                text: badgesTexts.billedInStages({
                    count: billing.oneTimeInStages.numStages()
                }),
                intent: 'type-one-time-in-stages'
            }),
        visitOngoing: (billing)=>({
                text: isClientPreview ? getRecurringTextForClientPreview(billing.recurrencePeriod) : badgesTexts.billedRecurringAutomatically({
                    period: billing.recurrencePeriod
                }),
                intent: 'type-ongoing'
            }),
        visitOnDemand: ()=>({
                text: isClientPreview ? getOnDemandTextForClientPreview() : badgesTexts.billedOnDemandManually(),
                intent: 'type-on-demand',
                skin: isClientPreview ? 'primary' : 'default'
            })
    });
}
function conditionalDiscountBadge(service, badgesTexts) {
    const expiration = serviceModel.limitedTimeDiscountOfferExpiration(service);
    if (!expiration) {
        return null;
    }
    return {
        skin: 'success',
        intent: 'conditional-discount',
        text: badgesTexts.conditionalDiscount()
    };
}
function bundleBadge(service, badgesTexts) {
    return serviceModel.isBundle(service) ? {
        intent: 'bundle',
        text: badgesTexts.bundle()
    } : null;
}
export function getServiceDescriptionBadges(service, agreementData, now, param) {
    let { texts, hideBillingDetailsBadge = false, hideDiscountBadge = false, hidePreapprovedBadge = false, hideProratedBadge = false, hidePausedBadge = false, hideOptionalBadge = false, hideLimitedTimesBadge = false, hidePriceIncreaseBadge = false, hideConditionalDiscountBadge = false, hideBundleBadge = false } = param;
    const billingTexts = texts.billing;
    const costDisplayTexts = texts.models.service.costDisplay;
    const commonTexts = texts.common;
    const badgesTexts = billingTexts.badges;
    return compact([
        !hideBundleBadge && bundleBadge(service, badgesTexts),
        !hideBillingDetailsBadge && billingDetailsBadge(service, {
            badgesTexts,
            isActiveAgreement: agreementData.isActiveAgreement(),
            effectiveDate: agreementData.effectiveDate,
            now
        }),
        !hideDiscountBadge && discountBadge(service, badgesTexts),
        !hidePreapprovedBadge && preapprovedBadge(service, billingTexts, costDisplayTexts),
        !hideProratedBadge && proratedBadge(service, badgesTexts),
        !hidePausedBadge && pausedBadge(service, badgesTexts),
        !hideOptionalBadge && optionalBadge(service, badgesTexts),
        !hideLimitedTimesBadge && limitedTimesBadge(service, badgesTexts, commonTexts),
        !hidePriceIncreaseBadge && priceIncreaseBadge(service, badgesTexts),
        !hideConditionalDiscountBadge && conditionalDiscountBadge(service, badgesTexts)
    ]);
}
